/**
 * Channel/function classification helpers for a pour's monitoring locations.
 *
 * Ambient Temp & Humidity hardware ('09' Digit Sensor / '10' ConXenviro)
 * serves the External Temperature and Humidity function; everything else is
 * concrete monitoring (maturity / mass concrete / TMC). Dual-purpose pours
 * mix both, so location lists and dashboard charts split on these helpers.
 */
import type { LocationStatus } from '../services/pours';

/** Humidity channels report %RH through the same value fields. */
export const isHumidityLocation = (l: LocationStatus): boolean =>
  (l.channel_type || '').toUpperCase() === 'HUMIDITY';

/** Ambient Temp & Humidity hardware ('09' Digit Sensor / '10' ConXenviro),
 *  or a concrete-hardware channel marked ambient (is_ambient). Ambient
 *  channels have no concrete temperature limits, so their profile bars drop
 *  the green-to-red gradient and the pour's limit markers — per channel, so
 *  concrete loggers on the same pour keep both. */
export const isAmbientLocation = (l: LocationStatus): boolean =>
  l.logger_type === '09' || l.logger_type === '10' || !!l.is_ambient;

/** A channel belonging to the ambient Temp & Humidity function — ambient
 *  hardware, or a humidity channel whose logger type is unknown. */
export const isAmbientChannel = (l: LocationStatus): boolean =>
  isAmbientLocation(l) || isHumidityLocation(l);

/** Weather-station hardware ('07', ZENTRA). Like ambient channels its
 *  readings describe site conditions, and its own data lives in the weather
 *  pipeline rather than the temperature track. */
export const isWeatherLocation = (l: LocationStatus): boolean =>
  l.logger_type === '07';

/** Any site-conditions channel — weather station or ambient Temp & Humidity.
 *  These measure the environment, not the concrete, so no concrete
 *  temperature semantics apply: no limit markers/plotlines, no limit
 *  gradient, no differentials, and they stay out of the concrete
 *  temperature overlay chart. */
export const isSiteConditionsChannel = (l: LocationStatus): boolean =>
  isWeatherLocation(l) || isAmbientChannel(l);

/** Monitoring-function group of a channel, in display order: Concrete
 *  Maturity (MonitorType '1'/legacy) → Temperature Only ('2') → ambient
 *  Temp & Humidity → weather station. Hardware type wins over MonitorType
 *  (the backend stores '2' on ambient/weather locations). */
const functionGroup = (l: LocationStatus): number => {
  if (isWeatherLocation(l)) return 3;
  if (isAmbientChannel(l)) return 2;
  if (l.monitor_type === '2') return 1;
  return 0;
};

const GROUP_LABELS = [
  'Concrete Maturity',
  'Temperature Only',
  'Ambient Temp & Humidity',
  'Weather Station',
];

/** The monitoring-function group a location belongs to, by name:
 *  "Concrete Maturity", "Temperature Only", "Ambient Temp & Humidity" or
 *  "Weather Station". */
export const locationFunctionLabel = (l: LocationStatus): string => GROUP_LABELS[functionGroup(l)];

/** What a channel measures, in words: "Humidity", "Weather station" or
 *  "Temperature". */
export const channelTypeLabel = (l: LocationStatus): string => {
  if (isHumidityLocation(l)) return 'Humidity';
  if (isWeatherLocation(l)) return 'Weather station';
  return 'Temperature';
};

/** Order a mixed list by monitoring function (maturity → temperature only →
 *  ambient Temp & Humidity → weather; stable within each group).
 *
 *  `groupLabels` maps row index → the quiet group label to render above that
 *  row. On multi-function pours EVERY group is labelled (including the
 *  first); single-function pours get an empty map — no labels, list renders
 *  exactly as before. */
export const orderByFunction = (
  locations: LocationStatus[],
): { ordered: LocationStatus[]; groupLabels: Map<number, string> } => {
  const ordered = [...locations].sort(
    (a, b) => functionGroup(a) - functionGroup(b),
  );
  const groupLabels = new Map<number, string>();
  for (let i = 0; i < ordered.length; i++) {
    if (i === 0 || functionGroup(ordered[i]) !== functionGroup(ordered[i - 1])) {
      groupLabels.set(i, GROUP_LABELS[functionGroup(ordered[i])]);
    }
  }
  if (groupLabels.size <= 1) groupLabels.clear();
  return { ordered, groupLabels };
};
